import React, { useState, useCallback, useContext } from 'react';
import Cropper from 'react-easy-crop';
import { Upload, X, Check, Image as ImageIcon, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { AuthContext } from '../App';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

/**
 * Utility to create an image element from a file
 */
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

/**
 * Get the cropped image from canvas
 */
async function getCroppedImg(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/png');
  });
}

const LogoUpload = ({ currentLogo, onLogoUpdate, disabled = false }) => {
  const { API } = useContext(AuthContext);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(1); // 1 = square, 16/9 = landscape, etc
  const { t } = useTranslation();

  // Extract base URL from API (remove /api suffix)
  const baseUrl = API.replace(/\/api\/?$/, '');

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileSelect = (e) => {
    if (disabled) {
      toast.error(t('messages.logoUpgradeRequired') || 'Custom logo is available on PRO plan');
      return;
    }
    
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(t('messages.selectImageFile'));
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('messages.fileSizeLimit'));
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      setPreviewUrl(reader.result);
      setCropDialogOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropSave = async () => {
    try {
      setUploading(true);
      
      // Get cropped image blob
      const croppedBlob = await getCroppedImg(previewUrl, croppedAreaPixels);
      
      // Create form data
      const formData = new FormData();
      formData.append('file', croppedBlob, selectedFile.name);

      // Upload to backend
      const response = await axios.post(`${API}/upload/logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success(t('messages.logoUploadSuccess'));
      
      // Update parent component with new logo URL
      if (onLogoUpdate) {
        onLogoUpdate(response.data.logo_url);
      }

      // Close dialog and reset state
      setCropDialogOpen(false);
      setPreviewUrl(null);
      setSelectedFile(null);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || t('messages.logoUploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleCropCancel = () => {
    setCropDialogOpen(false);
    setPreviewUrl(null);
    setSelectedFile(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    // Reset file input so same file can be selected again
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) fileInput.value = '';
  };

  return (
    <div className="space-y-4">
      {/* Upgrade Banner for STARTER plan */}
      {disabled && (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Lock className="w-5 h-5 text-purple-600" />
            <div>
              <p className="font-medium text-purple-900">{t('messages.logoProFeature') || 'Custom Logo - PRO Feature'}</p>
              <p className="text-sm text-purple-700">{t('messages.logoUpgradeDescription') || 'Upgrade to PRO to upload your restaurant logo'}</p>
            </div>
          </div>
          <Link to="/admin/upgrade">
            <Button variant="default" size="sm" className="bg-purple-600 hover:bg-purple-700">
              {t('common.upgrade') || 'Upgrade'}
            </Button>
          </Link>
        </div>
      )}
      
      {/* Current Logo Display */}
      <div className={`flex items-center gap-4 ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
          {currentLogo ? (
            <img 
              src={`${baseUrl}${currentLogo}`}
              alt={t('messages.businessLogoAlt')} 
              className="w-full h-full object-contain"
            />
          ) : (
            <ImageIcon className="w-12 h-12 text-gray-400" />
          )}
        </div>
        
        <div className="flex-1">
          <h4 className="font-medium mb-2">{t('messages.businessLogo')}</h4>
          <p className="text-sm text-gray-600 mb-3">
            {t('messages.logoUploadDescription')}
          </p>
          
          {/* File Input */}
          <label className={disabled ? 'cursor-not-allowed' : 'cursor-pointer'}>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileSelect}
              className="hidden"
              disabled={disabled}
            />
            <Button type="button" variant="outline" size="sm" asChild disabled={disabled}>
              <span>
                <Upload className="w-4 h-4 mr-2" />
                {currentLogo ? t('messages.changeLogo') : t('messages.uploadLogo')}
              </span>
            </Button>
          </label>
        </div>
      </div>

      {/* Crop Dialog */}
      <Dialog open={cropDialogOpen} onOpenChange={setCropDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('messages.cropYourLogo')}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Cropper */}
            <div className="relative h-screen max-h-96 bg-gray-100 rounded-lg overflow-hidden">
              {previewUrl && (
                <Cropper
                  image={previewUrl}
                  crop={crop}
                  zoom={zoom}
                  aspect={aspectRatio}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">{t('messages.logoFormat')}</label>
              <select 
                value={aspectRatio} 
                onChange={(e) => setAspectRatio(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value={1}>{t('messages.formatSquare')}</option>
                <option value={16/9}>{t('messages.formatLandscape169')}</option>
                <option value={4/3}>{t('messages.formatLandscape43')}</option>
                <option value={3/2}>{t('messages.formatLandscape32')}</option>
                <option value={9/16}>{t('messages.formatVertical')}</option>
              </select>
            </div>

            {/* Zoom Slider */}
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('messages.zoom')} ({zoom.toFixed(2)}x)</label>
              <input
                type="range"
                min={0.5}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="w-full"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCropCancel} disabled={uploading}>
              <X className="w-4 h-4 mr-2" />
              {t('messages.cancel')}
            </Button>
            <Button onClick={handleCropSave} disabled={uploading}>
              {uploading ? (
                <>{t('messages.uploading')}</>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {t('messages.saveLogo')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LogoUpload;
